import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SiteSettingsProvider } from "@/context/SiteSettingsContext";
import { CartProvider } from "@/context/CartContext";
import Layout from "@/components/Layout";
import RouteScrollManager from "@/components/RouteScrollManager";
import GuestPrompt from "@/components/GuestPrompt";

const lazyWithPreload = (loader) => {
  const Component = lazy(loader);
  Component.preload = loader;
  return Component;
};

import Home from "@/pages/Home";
const SellFoodLanding = lazyWithPreload(() => import("@/pages/SellFoodLanding"));
const Login = lazyWithPreload(() => import("@/pages/Login"));
const Register = lazyWithPreload(() => import("@/pages/Register"));
const VendorDetail = lazyWithPreload(() => import("@/pages/VendorDetail"));
const ProductDetail = lazyWithPreload(() => import("@/pages/ProductDetail"));
const Cart = lazyWithPreload(() => import("@/pages/Cart"));
const SupportCenter = lazyWithPreload(() => import("@/pages/SupportCenter"));
const AboutArcticPlate = lazyWithPreload(() => import("@/pages/AboutArcticPlate"));
const CustomerFeesPolicy = lazyWithPreload(() => import("@/pages/CustomerFeesPolicy"));
const Subscribe = lazyWithPreload(() => import("@/pages/Subscribe"));
const CustomerOrders = lazyWithPreload(() => import("@/pages/CustomerOrders"));
const Saved = lazyWithPreload(() => import("@/pages/Saved"));
const LocalPassport = lazyWithPreload(() => import("@/pages/LocalPassport"));
const MarketplaceConversation = lazyWithPreload(() => import("@/pages/MarketplaceConversation"));
const MarketingUnsubscribe = lazyWithPreload(() => import("@/pages/MarketingUnsubscribe"));
const OrderDetail = lazyWithPreload(() => import("@/pages/OrderDetail"));
import VendorLayout from "@/vendor/layout/VendorLayout";
const VendorMenuPage = lazyWithPreload(() => import("@/vendor/pages/VendorMenu"));
const VendorOrdersPage = lazyWithPreload(() => import("@/vendor/pages/VendorOrders"));
const VendorProductionPage = lazyWithPreload(() => import("@/vendor/pages/VendorProduction"));
const VendorNotificationsPage = lazyWithPreload(() => import("@/vendor/pages/VendorNotifications"));
const VendorDropsPage = lazyWithPreload(() => import("@/vendor/pages/VendorDrops"));
const VendorGrowthPage = lazyWithPreload(() => import("@/vendor/pages/VendorGrowth"));
const DropDetail = lazyWithPreload(() => import("@/pages/DropDetail"));
const VendorStorefrontPage = lazyWithPreload(() => import("@/vendor/pages/VendorStorefront"));
// Legacy /vendor/storefront/preview route removed 2026 — the seller now
// opens their real customer-facing storefront directly. Import kept out.
const VendorSetupPage = lazyWithPreload(() => import("@/vendor/pages/VendorSetup"));
const VendorDeliveryPage = lazyWithPreload(() => import("@/vendor/pages/VendorDelivery"));
const VendorAccountPage = lazyWithPreload(() => import("@/vendor/pages/VendorAccount"));
const AdminHome = lazyWithPreload(() => import("@/pages/admin/AdminHome"));
const AdminSiteEditor = lazyWithPreload(() => import("@/pages/AdminSiteEditor"));
const MarketplaceFees = lazyWithPreload(() => import("@/pages/admin/MarketplaceFees"));
const PermittedBusinessFees = lazyWithPreload(() => import("@/pages/PermittedBusinessFees"));
const AdminRefunds = lazyWithPreload(() => import("@/pages/AdminRefunds"));
const AdminNotifications = lazyWithPreload(() => import("@/pages/AdminNotifications"));
const AdminUserProfile = lazyWithPreload(() => import("@/pages/AdminUserProfile"));
const AdminCustomers = lazyWithPreload(() => import("@/pages/AdminCustomers"));
const AdminAccountRequests = lazyWithPreload(() => import("@/pages/AdminAccountRequests"));
const AdminStripeConnect = lazyWithPreload(() => import("@/pages/AdminStripeConnect"));
const AdminSellerApplications = lazyWithPreload(() => import("@/pages/AdminSellerApplications"));
const AdminOrders = lazyWithPreload(() => import("@/pages/AdminOrders"));
const AdminSellers = lazyWithPreload(() => import("@/pages/AdminSellers"));
const AdminDeliveryServiceAreas = lazyWithPreload(() => import("@/pages/AdminDeliveryServiceAreas"));
const AdminBusinessHours = lazyWithPreload(() => import("@/pages/AdminBusinessHours"));
const AdminFeatureAvailability = lazyWithPreload(() => import("@/pages/AdminFeatureAvailability"));
const AdminAnnouncements = lazyWithPreload(() => import("@/pages/AdminAnnouncements"));
const AdminHelpContent = lazyWithPreload(() => import("@/pages/AdminHelpContent"));
const AdminSupportInbox = lazyWithPreload(() => import("@/pages/AdminSupportInbox"));
const AdminSubscribers = lazyWithPreload(() => import("@/pages/AdminSubscribers"));
const AdminBuyerSettings = lazyWithPreload(() => import("@/pages/AdminBuyerSettings"));
const AdminSellerSettings = lazyWithPreload(() => import("@/pages/AdminSellerSettings"));
const AdminAuditHistory = lazyWithPreload(() => import("@/pages/AdminAuditHistory"));
const AdminPlatformSettingsMap = lazyWithPreload(() => import("@/pages/AdminPlatformSettingsMap"));
const AdminProfitSources = lazyWithPreload(() => import("@/pages/AdminProfitSources"));
// The owner-facing fee editor lives at /admin/marketplace-fees (imported above).
const AdminSecurity = lazyWithPreload(() => import("@/pages/AdminSecurity"));
// Historical /admin/tools redirects to /admin; password-reset controls live
// at /admin/security via AdminSecurity. Retired page source is in Git history.
const ArcticOpsCommandCenter = lazyWithPreload(() => import("@/pages/arcticops/ArcticOpsCommandCenter"));
const ArcticOpsSystemOverview = lazyWithPreload(() => import("@/pages/arcticops/SystemOverview"));
const ArcticOpsRepoIntel = lazyWithPreload(() => import("@/pages/arcticops/RepositoryIntelligence"));
const ArcticOpsArchMap = lazyWithPreload(() => import("@/pages/arcticops/ArchitectureMap"));
const ArcticOpsTimeline = lazyWithPreload(() => import("@/pages/arcticops/Timeline"));
const ArcticOpsSearch = lazyWithPreload(() => import("@/pages/arcticops/SemanticSearch"));
const ArcticOpsCouncil = lazyWithPreload(() => import("@/pages/arcticops/EngineeringCouncil"));
const ArcticOpsSeo = lazyWithPreload(() => import("@/pages/arcticops/SeoDashboard"));
const ArcticOpsSeoDrafts = lazyWithPreload(() => import("@/pages/arcticops/SeoDraftCenter"));
const ArcticOpsPrDrafts = lazyWithPreload(() => import("@/pages/arcticops/PrDraftCenter"));
const ArcticOpsSelfHealing = lazyWithPreload(() => import("@/pages/arcticops/SelfHealingCenter"));
const ArcticOpsStripeGuardian = lazyWithPreload(() => import("@/pages/arcticops/StripeGuardian"));
const ArcticOpsBusinessIntel = lazyWithPreload(() => import("@/pages/arcticops/BusinessIntelligence"));
const ArcticOpsExecutive = lazyWithPreload(() => import("@/pages/arcticops/ExecutiveIntelligence"));
const ArcticOpsForecasting = lazyWithPreload(() => import("@/pages/arcticops/ForecastingCenter"));
const ArcticOpsCoalescence = lazyWithPreload(() => import("@/pages/arcticops/CoalescenceCenter"));
const ArcticOpsPreflight = lazyWithPreload(() => import("@/pages/arcticops/PreflightCenter"));
const ArcticOpsDigest = lazyWithPreload(() => import("@/pages/arcticops/DigestCenter"));
const ArcticOpsBriefing = lazyWithPreload(() => import("@/pages/arcticops/BriefingCenter"));
const ArcticOpsDecisionQueue = lazyWithPreload(() => import("@/pages/arcticops/DecisionQueueCenter"));
const ArcticOpsOwnership = lazyWithPreload(() => import("@/pages/arcticops/OwnershipCenter"));
const ArcticOpsRecsFeed = lazyWithPreload(() => import("@/pages/arcticops/RecommendationsFeed"));
const ArcticOpsStatusPage = lazyWithPreload(() => import("@/pages/arcticops/ArcticOpsStatus"));
const IndraBuilderCenter = lazyWithPreload(() => import("@/pages/arcticops/IndraBuilderCenter"));
const AutonomyCenter = lazyWithPreload(() => import("@/pages/arcticops/AutonomyCenter"));
const CommandCenter = lazyWithPreload(() => import("@/pages/arcticops/CommandCenter"));
const Vision = lazyWithPreload(() => import("@/pages/arcticops/Vision"));
const Account = lazyWithPreload(() => import("@/pages/Account"));
const ChangePassword = lazyWithPreload(() => import("@/pages/ChangePassword"));
const ForgotPassword = lazyWithPreload(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazyWithPreload(() => import("@/pages/ResetPassword"));
const VendorEmbed = lazyWithPreload(() => import("@/pages/VendorEmbed"));

const BUYER_ROUTE_PRELOAD = [
  SellFoodLanding, Login, Register, ForgotPassword, VendorDetail, ProductDetail,
  Cart, DropDetail, SupportCenter, CustomerOrders, Saved, LocalPassport, MarketplaceConversation, Account,
];
const VENDOR_ROUTE_PRELOAD = [
  VendorOrdersPage, VendorMenuPage, VendorNotificationsPage,
  VendorGrowthPage, VendorStorefrontPage, VendorDeliveryPage, VendorAccountPage,
];
const ADMIN_ROUTE_PRELOAD = [
  AdminHome, AdminNotifications, AdminOrders, AdminSellers, AdminCustomers,
  AdminSecurity, AdminSiteEditor, AdminStripeConnect, Account,
];

// Stable role arrays — declared once at module scope so the `roles` prop on
// <Protected> doesn't allocate a fresh array on every App render.
const ROLES_CUSTOMER = ["customer"];
const ROLES_VENDOR = ["vendor"];
const ROLES_ADMIN = ["admin"];

function RoutePaneLoading() {
  return (
    <div
      className="min-h-[45vh] px-5 py-8"
      data-testid="route-pane-loading"
      aria-busy="true"
      aria-label="Loading page"
    >
      <div className="mx-auto max-w-5xl animate-pulse space-y-4">
        <div className="h-7 w-44 rounded-lg bg-[#f3eee8]" />
        <div className="h-28 rounded-2xl bg-[#f7f2ec]" />
        <div className="h-28 rounded-2xl bg-[#f7f2ec]" />
      </div>
    </div>
  );
}

function Protected({ children, roles, promptTitle, promptAction }) {
  const { user, loading } = useAuth();
  if (loading) {
    return <div className="ap-shell flex items-center justify-center p-10 text-sm text-[var(--ap-ink-soft)]" data-testid="auth-loading">Loading…</div>;
  }
  // Guest hitting an account-only feature — show the friendly prompt, don't redirect.
  if (!user) return <GuestPrompt title={promptTitle} action={promptAction} />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function Shell() {
  const { user, loading } = useAuth();

  useEffect(() => {
    if (loading || typeof window === "undefined") return undefined;
    const connection = typeof navigator !== "undefined" ? navigator.connection : null;
    const effectiveType = String(connection?.effectiveType || "").toLowerCase();
    if (connection?.saveData || effectiveType === "slow-2g" || effectiveType === "2g") {
      return undefined;
    }

    const targets = user?.role === "vendor"
      ? VENDOR_ROUTE_PRELOAD
      : user?.role === "admin"
        ? ADMIN_ROUTE_PRELOAD
        : BUYER_ROUTE_PRELOAD;

    // Keep speculative route loading from stealing bandwidth/CPU from the
    // screen the customer is actually looking at. Mobile Safari does not
    // reliably expose requestIdleCallback, so the old 650ms fallback started
    // downloading a large stack of route chunks while homepage media was
    // still loading.
    const warmTargets = targets.slice(0, 4);
    const warm = () => {
      warmTargets.forEach((Component) => {
        try {
          const pending = Component?.preload?.();
          if (pending && typeof pending.catch === "function") pending.catch(() => {});
        } catch {
          // A failed speculative chunk must never interrupt the active screen.
        }
      });
    };

    let idleId = null;
    let timerId = null;
    if (typeof window.requestIdleCallback === "function") {
      idleId = window.requestIdleCallback(warm, { timeout: 5000 });
    } else {
      timerId = window.setTimeout(warm, 3500);
    }
    return () => {
      if (idleId !== null && typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(idleId);
      }
      if (timerId !== null) window.clearTimeout(timerId);
    };
  }, [loading, user?.role]);

  if (loading) return <div className="ap-shell flex items-center justify-center p-10 text-sm text-[var(--ap-ink-soft)]">Loading…</div>;

  // Force-reset gate: any authenticated user with must_reset_password=true is
  // funneled to /change-password until they clear the flag. We let the page
  // itself render so they can actually submit the change-password form.
  const needsReset =
    user && user.must_reset_password === true &&
    typeof window !== "undefined" &&
    window.location &&
    window.location.pathname !== "/change-password";
  if (needsReset) {
    return <Navigate to="/change-password" replace />;
  }

  return (
    <Suspense
      fallback={<div className="ap-shell flex items-center justify-center p-10 text-sm text-[var(--ap-ink-soft)]">Loading…</div>}
    >
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/register" element={user ? <Navigate to="/" replace /> : <Register />} />
      <Route path="/forgot-password" element={user ? <Navigate to="/" replace /> : <ForgotPassword />} />
      <Route path="/reset-password" element={user ? <Navigate to="/" replace /> : <ResetPassword />} />
      <Route path="/change-password" element={user ? <ChangePassword /> : <Navigate to="/login" replace />} />
      <Route path="/embed/vendor/:id" element={<VendorEmbed />} />
      <Route
        path="/admin/arcticops"
        element={
          <Protected roles={ROLES_ADMIN} promptTitle="Admin only">
            <ArcticOpsCommandCenter />
          </Protected>
        }
      >
        <Route index element={<Navigate to="command" replace />} />
        <Route path="executive" element={<ArcticOpsExecutive />} />
        <Route path="forecasting" element={<ArcticOpsForecasting />} />
        <Route path="coalescence" element={<ArcticOpsCoalescence />} />
        <Route path="preflight" element={<ArcticOpsPreflight />} />
        <Route path="digest" element={<ArcticOpsDigest />} />
        <Route path="briefing" element={<ArcticOpsBriefing />} />
        <Route path="council-queue" element={<ArcticOpsDecisionQueue queue="council" />} />
        <Route path="healing-queue" element={<ArcticOpsDecisionQueue queue="healing" />} />
        <Route path="pr-queue" element={<ArcticOpsDecisionQueue queue="pr" />} />
        <Route path="ownership" element={<ArcticOpsOwnership />} />
        <Route path="overview" element={<ArcticOpsSystemOverview />} />
        <Route path="repository" element={<ArcticOpsRepoIntel />} />
        <Route path="architecture" element={<ArcticOpsArchMap />} />
        <Route path="timeline" element={<ArcticOpsTimeline />} />
        <Route path="search" element={<ArcticOpsSearch />} />
        <Route path="council" element={<ArcticOpsCouncil />} />
        <Route path="seo" element={<ArcticOpsSeo />} />
        <Route path="seo/drafts" element={<ArcticOpsSeoDrafts />} />
        <Route path="pr/drafts" element={<ArcticOpsPrDrafts />} />
        <Route path="self-healing" element={<ArcticOpsSelfHealing />} />
        <Route path="stripe" element={<ArcticOpsStripeGuardian />} />
        <Route path="business" element={<ArcticOpsBusinessIntel />} />
        <Route path="recommendations" element={<ArcticOpsRecsFeed />} />
        <Route path="status" element={<ArcticOpsStatusPage />} />
        <Route path="indra-builder" element={<IndraBuilderCenter />} />
        <Route path="autonomy" element={<AutonomyCenter />} />
        <Route path="command" element={<CommandCenter />} />
        <Route path="vision" element={<Vision />} />
      </Route>
      <Route
        path="/*"
        element={
          <Layout>
            <Suspense fallback={<RoutePaneLoading />}>
            <Routes>
              {/* Public — guests + customers + admins browse the marketplace.
                  Vendors are intentionally redirected to their own workspace
                  so the customer feed never bleeds into vendor mode. */}
              <Route
                path="/"
                element={user?.role === "vendor" ? <Navigate to="/vendor" replace /> : <Home />}
              />
              <Route path="/sell" element={<Navigate to="/sell-food" replace />} />
              <Route path="/sell-food" element={user?.role === "vendor" ? <Navigate to="/vendor" replace /> : <SellFoodLanding />} />
              <Route
                path="/vendor/:id"
                element={<VendorDetail />}
              />
              <Route
                path="/vendor/:vendorId/product/:itemId"
                element={<ProductDetail />}
              />
              <Route path="/cart" element={<Cart />} />
              <Route path="/drop/:dropId" element={<DropDetail />} />
              <Route path="/about" element={<AboutArcticPlate />} />
              <Route path="/fees-delivery" element={<CustomerFeesPolicy />} />
              <Route path="/support" element={<SupportCenter />} />
              <Route path="/subscribe" element={<Subscribe />} />
              <Route path="/unsubscribe" element={<MarketingUnsubscribe />} />
              <Route path="/orders/g/:id" element={<OrderDetail />} />

              {/* Account-only — guests see the friendly prompt instead of a redirect */}
              <Route
                path="/orders"
                element={
                  <Protected roles={ROLES_CUSTOMER} promptTitle="Track your orders" promptAction="Create free account">
                    <CustomerOrders />
                  </Protected>
                }
              />
              <Route
                path="/saved"
                element={
                  <Protected roles={ROLES_CUSTOMER} promptTitle="Save local food" promptAction="Create free account">
                    <Saved initialTab="saved" />
                  </Protected>
                }
              />
              <Route
                path="/following"
                element={
                  <Protected roles={ROLES_CUSTOMER} promptTitle="Follow local food businesses" promptAction="Create free account">
                    <Saved initialTab="following" />
                  </Protected>
                }
              />
              <Route
                path="/alerts"
                element={
                  <Protected roles={ROLES_CUSTOMER} promptTitle="See your local food alerts" promptAction="Create free account">
                    <Saved initialTab="alerts" />
                  </Protected>
                }
              />
              <Route
                path="/passport"
                element={
                  <Protected roles={ROLES_CUSTOMER} promptTitle="Your Local Passport" promptAction="Create free account">
                    <LocalPassport />
                  </Protected>
                }
              />
              <Route
                path="/messages/:id"
                element={
                  <Protected promptTitle="Sign in to continue this conversation">
                    <MarketplaceConversation />
                  </Protected>
                }
              />
              <Route
                path="/orders/:id"
                element={
                  <Protected promptTitle="Sign in to view this order">
                    <OrderDetail />
                  </Protected>
                }
              />
              <Route
                path="/vendor"
                element={
                  <Protected roles={ROLES_VENDOR} promptTitle="Vendor workspace">
                    <VendorLayout />
                  </Protected>
                }
              >
                {/* Storefront is the seller landing page. Keep the old
                    dashboard URL as a compatibility redirect only. */}
                <Route index element={<Navigate to="/vendor/storefront" replace />} />
                <Route path="dashboard" element={<Navigate to="/vendor/storefront" replace />} />
                <Route path="menu" element={<VendorMenuPage />} />
                <Route path="orders" element={<VendorOrdersPage />} />
                <Route path="production" element={<VendorProductionPage />} />
                <Route path="notifications" element={<VendorNotificationsPage />} />
                <Route path="drops" element={<VendorDropsPage />} />
                <Route path="growth" element={<VendorGrowthPage />} />
                <Route path="storefront" element={<VendorStorefrontPage />} />
                {/* Legacy /vendor/storefront/preview redirects to the vendor's
                    actual customer-facing storefront so the seller always sees
                    the real page a customer sees — not a fake inline mock. */}
                <Route path="storefront/preview" element={<Navigate to="/vendor/storefront" replace />} />
                <Route path="setup" element={<VendorSetupPage />} />
                <Route path="delivery" element={<VendorDeliveryPage />} />
                <Route path="profit" element={<Navigate to="/vendor/storefront" replace />} />
                <Route path="account" element={<VendorAccountPage />} />
              </Route>
              <Route
                path="/admin"
                element={
                  <Protected roles={ROLES_ADMIN} promptTitle="Admin only">
                    <AdminHome />
                  </Protected>
                }
              />
              <Route
                path="/admin/dashboard"
                element={<Navigate to="/admin" replace />}
              />
              <Route
                path="/admin/site-editor"
                element={
                  <Protected roles={ROLES_ADMIN} promptTitle="Admin only">
                    <AdminSiteEditor />
                  </Protected>
                }
              />
              <Route
                path="/admin/marketplace-fees"
                element={
                  <Protected roles={ROLES_ADMIN} promptTitle="Admin only">
                    <MarketplaceFees />
                  </Protected>
                }
              />
              <Route
                path="/admin/permitted-business-fees"
                element={
                  <Protected roles={ROLES_ADMIN} promptTitle="Admin only">
                    <PermittedBusinessFees />
                  </Protected>
                }
              />
              <Route
                path="/admin/tools"
                element={<Navigate to="/admin/security" replace />}
              />
              <Route
                path="/admin/security"
                element={
                  <Protected roles={ROLES_ADMIN} promptTitle="Admin only">
                    <AdminSecurity />
                  </Protected>
                }
              />
              <Route
                path="/admin/stripe-connect"
                element={
                  <Protected roles={ROLES_ADMIN} promptTitle="Admin only">
                    <AdminStripeConnect />
                  </Protected>
                }
              />
              <Route
                path="/admin/fee-engine"
                element={<Navigate to="/admin/marketplace-fees" replace />}
              />
              <Route
                path="/admin/seller-applications"
                element={
                  <Protected roles={ROLES_ADMIN} promptTitle="Admin only">
                    <AdminSellerApplications />
                  </Protected>
                }
              />
              <Route
                path="/admin/sellers"
                element={
                  <Protected roles={ROLES_ADMIN} promptTitle="Admin only">
                    <AdminSellers />
                  </Protected>
                }
              />
              <Route
                path="/admin/orders"
                element={
                  <Protected roles={ROLES_ADMIN} promptTitle="Admin only">
                    <AdminOrders />
                  </Protected>
                }
              />
              <Route
                path="/admin/delivery-service-areas"
                element={
                  <Protected roles={ROLES_ADMIN} promptTitle="Admin only">
                    <AdminDeliveryServiceAreas />
                  </Protected>
                }
              />
              <Route
                path="/admin/business-hours"
                element={
                  <Protected roles={ROLES_ADMIN} promptTitle="Admin only">
                    <AdminBusinessHours />
                  </Protected>
                }
              />
              <Route
                path="/admin/feature-availability"
                element={
                  <Protected roles={ROLES_ADMIN} promptTitle="Admin only">
                    <AdminFeatureAvailability />
                  </Protected>
                }
              />
              <Route
                path="/admin/announcements"
                element={
                  <Protected roles={ROLES_ADMIN} promptTitle="Admin only">
                    <AdminAnnouncements />
                  </Protected>
                }
              />
              <Route
                path="/admin/help-content"
                element={
                  <Protected roles={ROLES_ADMIN} promptTitle="Admin only">
                    <AdminHelpContent />
                  </Protected>
                }
              />
              <Route
                path="/admin/support"
                element={
                  <Protected roles={ROLES_ADMIN} promptTitle="Admin only">
                    <AdminSupportInbox />
                  </Protected>
                }
              />
              <Route path="/admin/subscribers" element={<Protected roles={ROLES_ADMIN} promptTitle="Admin only"><AdminSubscribers /></Protected>} />
              <Route path="/admin/buyer-settings" element={<Protected roles={ROLES_ADMIN} promptTitle="Admin only"><AdminBuyerSettings /></Protected>} />
              <Route path="/admin/seller-settings" element={<Protected roles={ROLES_ADMIN} promptTitle="Admin only"><AdminSellerSettings /></Protected>} />
              <Route path="/admin/profit-sources" element={<Protected roles={ROLES_ADMIN} promptTitle="Admin only"><AdminProfitSources /></Protected>} />
              <Route path="/admin/audit-history" element={<Protected roles={ROLES_ADMIN} promptTitle="Admin only"><AdminAuditHistory /></Protected>} />
              <Route path="/admin/platform/source-of-truth" element={<Protected roles={ROLES_ADMIN} promptTitle="Admin only"><AdminPlatformSettingsMap /></Protected>} />
              <Route
                path="/admin/refunds"
                element={
                  <Protected roles={ROLES_ADMIN} promptTitle="Admin only">
                    <AdminRefunds />
                  </Protected>
                }
              />
              <Route
                path="/admin/notifications"
                element={
                  <Protected roles={ROLES_ADMIN} promptTitle="Admin only">
                    <AdminNotifications />
                  </Protected>
                }
              />
              <Route
                path="/admin/customers"
                element={
                  <Protected roles={ROLES_ADMIN} promptTitle="Admin only">
                    <AdminCustomers />
                  </Protected>
                }
              />
              <Route
                path="/admin/users/:userId"
                element={
                  <Protected roles={ROLES_ADMIN} promptTitle="Admin only">
                    <AdminUserProfile />
                  </Protected>
                }
              />
              <Route
                path="/admin/account-requests"
                element={
                  <Protected roles={ROLES_ADMIN} promptTitle="Admin only">
                    <AdminAccountRequests />
                  </Protected>
                }
              />
              <Route
                path="/admin/deletion-requests"
                element={
                  <Protected roles={ROLES_ADMIN} promptTitle="Admin only">
                    <AdminAccountRequests />
                  </Protected>
                }
              />
              <Route
                path="/account"
                element={
                  <Protected promptTitle="Your account">
                    <Account />
                  </Protected>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </Suspense>
          </Layout>
        }
      />
    </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <SiteSettingsProvider>
            <RouteScrollManager />
            <Shell />
            <Toaster position="top-center" richColors />
          </SiteSettingsProvider>
        </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}